import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import NoVNCContainer from "components/NoVNCContainer/NoVNCContainer";
import synkChainScanPageStore from "../store/store.SynkChainScanPage";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "lib/utils";

const CheckIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={cn("w-6 h-6 ", className)}
    >
      <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
};

const CheckFilled = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("w-6 h-6 ", className)}
    >
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
        clipRule="evenodd"
      />
    </svg>
  );
};

const LoadingMessage: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const [displayStep, setDisplayStep] = useState(currentStep);

  useEffect(() => {
    if (currentStep === 6) {
      setDisplayStep(6);
      return;
    }

    const interval = setInterval(() => {
      setDisplayStep((prev) => {
        if (prev >= 6) return prev;
        return prev + 1;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [currentStep]);

  const loadingStates = [
    { text: "Let me take my time to set everything up..." },
    { text: "Creating your secure container..." },
    { text: "Hmm, I remember this URL. Give me a few seconds..." },
    { text: "Initializing secure connection..." },
    { text: "Setting up encryption protocols..." },
    { text: "Almost there, just a few more seconds..." },
    { text: "Perfecting the pixels, almost there!" },
  ];

  return (
    <div className="flex relative justify-start max-w-xl mx-auto flex-col mt-40">
      {loadingStates.map((loadingState, index) => {
        const distance = Math.abs(index - displayStep);
        const opacity = Math.max(1 - distance * 0.2, 0);

        return (
          <motion.div
            key={index}
            className={cn("text-left flex gap-2 mb-4")}
            initial={{ opacity: 0, y: -(displayStep * 40) }}
            animate={{ opacity: opacity, y: -(displayStep * 40) }}
            transition={{ duration: 0.5 }}
          >
            <div>
              {index > displayStep && <CheckIcon className="text-white" />}
              {index <= displayStep && (
                <CheckFilled
                  className={cn(
                    "text-white",
                    displayStep === index && "text-synkColor opacity-100"
                  )}
                />
              )}
            </div>
            <span
              className={cn(
                "text-white",
                displayStep === index && "text-synkColor opacity-100"
              )}
            >
              {loadingState.text}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div className="w-full h-full absolute inset-0 z-[100] flex items-center justify-center bg-black/[0.96] antialiased bg-grid-white/[0.02] relative overflow-hidden">
      <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:44px_44px]"></div>

      <div className="absolute inset-0 bg-customGray animate-fadeOut z-0 pointer-events-none"></div>
      <div
        className="absolute inset-0 bg-cover bg-no-repeat animate-fadeInWithBlurDelayed opacity-0"
        style={{
          backgroundImage: 'url("/images/Ellipse1.png")',
          zIndex: 0,
        }}
      ></div>

      <div className="flex flex-col items-center p-4 px-8 max-w-7xl relative z-10">
        <h1 className="text-5xl md:text-7xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-b from-synkColor/10 to-synkColor/50 py-1">
          Oops! That didn't go as planned.
        </h1>
        <p className="mt-4 font-normal text-base text-neutral-300 max-w-xl text-center">
          {message}
        </p>
      </div>
    </div>
  );
};

interface ExplorerIframeProps {
  src?: string;
  title?: string;
}

const ExplorerIframe: React.FC<ExplorerIframeProps> = observer(() => {
  const selectedExplorer = synkChainScanPageStore.selectedExplorer;

  if (synkChainScanPageStore.error && !selectedExplorer?.novncConfig) {
    return (
      <div className="flex h-full w-full items-center justify-center px-2 pb-2">
        <div className="rounded-medium relative h-full w-full overflow-hidden bg-black/80">
          <ErrorMessage message={synkChainScanPageStore.error} />
        </div>
      </div>
    );
  }

  if (synkChainScanPageStore.isLoading || !synkChainScanPageStore.isVNCReady) {
    return (
      <div className="flex h-full w-full items-center justify-center px-2 pb-2">
        <div className="rounded-medium relative h-full w-full overflow-hidden bg-default-50/50">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full h-full absolute inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-2xl"
            >
              <div
                className="absolute inset-0 bg-cover bg-no-repeat"
                style={{
                  backgroundImage: 'url("/images/Ellipse1.png")',
                  zIndex: 0,
                }}
              ></div>

              <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,#22dcd105_1px,transparent_1px),linear-gradient(to_bottom,#22dcd105_1px,transparent_1px)] bg-[size:104px_104px]"></div>

              <div className="h-96 relative">
                <LoadingMessage
                  currentStep={synkChainScanPageStore.loadingStep}
                />
              </div>

              <div className="bg-gradient-to-t inset-x-0 z-20 bottom-0 bg-dark h-full absolute [mask-image:radial-gradient(900px_at_bottom,transparent_50%,white)]" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  if (selectedExplorer?.novncConfig) {
    return (
      <div className="flex h-full w-full items-center justify-center px-2 pb-2">
        <div className="rounded-medium relative h-full w-full overflow-hidden">
          <NoVNCContainer
            url={selectedExplorer.novncConfig.vncUrl}
            className="relative h-full w-full"
            showDrawer={false}
          />
        </div>
      </div>
    );
  }

  return null;
});

export default ExplorerIframe;
